import * as React from "react";
import { ChevronDown, ChevronRight, Info } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { getChunkDetail } from "@hp/api-client";
import type { Finding, QuestionTrace, TraceCoverageRow, TracePassage, TraceVector } from "@hp/api-client";

type Tone = "zinc" | "emerald" | "rose" | "sky" | "amber";

export function Tag({ children, tone = "zinc" }: { children: React.ReactNode; tone?: Tone }) {
  const tones: Record<Tone, string> = {
    zinc: "bg-zinc-100 text-zinc-700",
    emerald: "bg-emerald-50 text-emerald-800",
    rose: "bg-rose-50 text-rose-800",
    sky: "bg-sky-50 text-sky-800",
    amber: "bg-amber-50 text-amber-800",
  };
  return <span className={["rounded px-1.5 py-0.5 text-xs font-medium", tones[tone]].join(" ")}>{children}</span>;
}

/** A fold: a heading with a count, closed by default, that opens to show its content. `hint` adds an "i" button that shows a one-line plain definition. */
export function Fold({ title, count, children, open = false, hint }: { title: string; count?: number | string; children: React.ReactNode; open?: boolean; hint?: string }) {
  const [shown, setShown] = React.useState(open);
  const [explained, setExplained] = React.useState(false);
  return (
    <div className="rounded-md border border-zinc-200 bg-white">
      <div className="flex items-center">
        <button
          type="button"
          onClick={() => setShown((v) => !v)}
          aria-expanded={shown}
          className="flex flex-1 items-center gap-1.5 px-3 py-2 text-left text-xs font-semibold text-zinc-700 hover:bg-zinc-50"
        >
          {shown ? <ChevronDown className="h-3.5 w-3.5" aria-hidden /> : <ChevronRight className="h-3.5 w-3.5" aria-hidden />}
          {title}
          {count !== undefined ? <span className="ml-1 font-normal text-zinc-500">({count})</span> : null}
        </button>
        {hint ? (
          <button
            type="button"
            onClick={() => setExplained((v) => !v)}
            aria-expanded={explained}
            aria-label={`What is "${title}"?`}
            title="What is this?"
            className="mr-2 rounded p-1 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700"
          >
            <Info className="h-3.5 w-3.5" aria-hidden />
          </button>
        ) : null}
      </div>
      {explained && hint ? <p className="border-t border-zinc-100 bg-sky-50 px-3 py-2 text-xs text-sky-900">{hint}</p> : null}
      {shown ? <div className="border-t border-zinc-100 px-3 py-2">{children}</div> : null}
    </div>
  );
}

function NotRecorded() {
  return <p className="text-xs text-zinc-500">Not recorded for this question.</p>;
}

const SEVERITY_ORDER: Record<string, number> = { high: 0, medium: 1, low: 2, info: 3 };
const SEVERITY_TONE: Record<string, Tone> = { high: "rose", medium: "amber", low: "sky", info: "zinc" };

export function severityTone(s: string): Tone {
  return SEVERITY_TONE[s] ?? "zinc";
}

/** The automatic checks: what could be missing from this answer. */
export function DiagnosisPanel({ findings }: { findings: Finding[] | undefined }) {
  if (!findings?.length) return null;
  const sorted = [...findings].sort((a, b) => (SEVERITY_ORDER[a.severity] ?? 9) - (SEVERITY_ORDER[b.severity] ?? 9));
  return (
    <section className="rounded-md border border-zinc-200 bg-white px-3 py-2" aria-label="What could be missing">
      <h3 className="text-xs font-semibold text-zinc-800">What could be missing</h3>
      <ul className="mt-1 space-y-2">
          {sorted.map((f, i) => (
            <li key={`${f.code}-${i}`} className="text-xs text-zinc-700">
              <div className="flex flex-wrap items-center gap-1.5">
                <Tag tone={severityTone(f.severity)}>{f.severity}</Tag>
                <span className="font-semibold text-zinc-900">{f.title}</span>
                <span className="font-mono text-[10px] text-zinc-400">{f.code}</span>
              </div>
              {f.detail ? <p className="mt-0.5">{f.detail}</p> : null}
              {f.hint ? <p className="mt-0.5 text-zinc-500">Hint: {f.hint}</p> : null}
              {f.evidence?.length ? (
                <ul className="mt-0.5 list-disc space-y-0.5 pl-5 text-zinc-500">
                  {f.evidence.map((e, j) => (
                    <li key={j}>{e}</li>
                  ))}
                </ul>
              ) : null}
            </li>
          ))}
        </ul>
    </section>
  );
}

/** Question label and scope chips. */
export function TraceHeader({ trace }: { trace: QuestionTrace }) {
  const { label, scope } = trace;
  if (!label && !scope) return null;
  const chips: React.ReactNode[] = [];
  if (label) {
    const tone = label.quality === "well-formed" ? "emerald" : label.quality === "vague" ? "rose" : "amber";
    chips.push(
      <Tag key="label" tone={tone}>
        {label.quality}
        {label.reason ? `: ${label.reason}` : ""}
      </Tag>,
    );
  }
  (scope?.contracts ?? []).forEach((c) => chips.push(<Tag key={`c-${c}`} tone="sky">contract {c}</Tag>));
  (scope?.states ?? []).forEach((c) => chips.push(<Tag key={`s-${c}`}>{c}</Tag>));
  (scope?.periods ?? []).forEach((c) => chips.push(<Tag key={`p-${c}`}>{c}</Tag>));
  if (scope?.as_of) chips.push(<Tag key="asof">as of {scope.as_of}</Tag>);
  if (scope?.clarify?.asked) chips.push(<Tag key="cl" tone="amber">clarification asked{scope.clarify.reason ? `: ${scope.clarify.reason}` : ""}</Tag>);
  if (scope?.borrowed_from_earlier) chips.push(<Tag key="bo" tone="amber">borrowed from an earlier question: {scope.borrowed_from_earlier}</Tag>);
  if (!chips.length) return null;
  return <div className="flex flex-wrap gap-1.5">{chips}</div>;
}

type SortKey = "score" | "none";

/** Every passage the writer saw, as a sortable table with a text preview on expand. */
export function ChunksTable({ items, vector }: { items: TracePassage[]; vector?: TraceVector }) {
  const near = React.useMemo(() => new Map((vector?.nearest ?? []).map((n) => [n.row_id, n])), [vector]);
  const [sort, setSort] = React.useState<SortKey>("none");
  const [openRow, setOpenRow] = React.useState<number | null>(null);
  const rows = React.useMemo(() => {
    const indexed = items.map((p, i) => ({ p, i }));
    if (sort === "score") indexed.sort((a, b) => (b.p.score ?? 0) - (a.p.score ?? 0));
    return indexed;
  }, [items, sort]);
  if (!items.length) return <p className="text-xs text-zinc-500">None.</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-xs text-zinc-700">
        <thead className="text-zinc-500">
          <tr>
            <th className="py-1 pr-2 font-medium">File</th>
            <th className="pr-2 font-medium">Page</th>
            <th className="pr-2 font-medium">Where</th>
            <th className="pr-2 font-medium">Layer</th>
            <th className="pr-2 font-medium">Kind</th>
            <th className="pr-2 font-medium">
              <button type="button" onClick={() => setSort((s) => (s === "score" ? "none" : "score"))} className="font-medium underline decoration-dotted">
                Score{sort === "score" ? " ▼" : ""}
              </button>
            </th>
            <th className="pr-2 font-medium">Why included</th>
            <th className="pr-2 font-medium">Chars</th>
            <th className="pr-2 font-medium">Vector</th>
            <th className="font-medium">Flags</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ p, i }) => (
            <React.Fragment key={`${p.key}-${i}`}>
              <tr className="cursor-pointer border-t border-zinc-100 align-top hover:bg-zinc-50" onClick={() => setOpenRow((v) => (v === i ? null : i))}>
                <td className="py-1 pr-2 font-medium text-zinc-900">{p.file}</td>
                <td className="pr-2">{p.page ?? ""}</td>
                <td className="pr-2">{p.section_path || p.where}</td>
                <td className="pr-2">{p.layer}</td>
                <td className="pr-2">{p.kind ?? ""}</td>
                <td className="pr-2">{p.score}</td>
                <td className="pr-2">{p.why ?? ""}</td>
                <td className="pr-2">{p.chars ?? ""}</td>
                <td className="pr-2 whitespace-nowrap">
                  {p.row_id && near.has(p.row_id) ? (
                    <Tag tone="emerald">#{(near.get(p.row_id)?.rank ?? 0) + 1} · {near.get(p.row_id)?.sim}</Tag>
                  ) : (
                    <span className="text-zinc-300">-</span>
                  )}
                </td>
                <td className="space-x-1 whitespace-nowrap">
                  {p.party ? <Tag tone="amber">party</Tag> : null}
                  {p.ocr ? <Tag tone="rose">ocr</Tag> : null}
                  {p.pinned ? <Tag tone="sky">pinned</Tag> : null}
                  {p.expand ? <Tag>expanded</Tag> : null}
                </td>
              </tr>
              {openRow === i ? (
                <tr>
                  <td colSpan={10} className="bg-zinc-50 px-2 py-1 text-zinc-600">
                    <ChunkDetailBox passage={p} nearest={p.row_id ? near.get(p.row_id) : undefined} />
                  </td>
                </tr>
              ) : null}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** One chunk opened: its whole text and the vector stored for it. */
function ChunkDetailBox({ passage: p, nearest }: { passage: TracePassage; nearest?: { rank: number; sim: number } }) {
  const known = !!p.row_id && (p.kind === "segment" || p.kind === "section");
  const detail = useQuery({ queryKey: ["genie-chunk", p.kind, p.row_id], queryFn: () => getChunkDetail(p.kind as string, p.row_id as string), enabled: known, retry: false });
  const d = detail.data;
  return (
    <div className="space-y-1.5">
      <p>
        <span className="font-mono text-zinc-400">{p.key}</span> {p.contract_id ? <Tag>{p.contract_id}</Tag> : null}{" "}
        {nearest ? <Tag tone="emerald">meaning search: #{nearest.rank + 1}, similarity {nearest.sim}</Tag> : <Tag>not found by meaning search</Tag>}
      </p>
      <p className="whitespace-pre-wrap text-zinc-700">{d?.text || p.text || "No text preview."}</p>
      {!known ? <p className="text-zinc-500">The full text and vector are not available for this row (recorded before they were kept).</p> : null}
      {detail.isLoading ? <p className="text-zinc-500">Loading the chunk...</p> : null}
      {detail.error ? <p className="text-rose-700">The chunk could not be loaded.</p> : null}
      {d ? (
        <div className="rounded border border-zinc-200 bg-white px-2 py-1.5">
          <p className="font-semibold text-zinc-700">Vector embedding</p>
          {d.has_vector ? (
            <>
              <p>
                model {d.model || "unknown"} · {d.dim ?? "?"} numbers · length {d.norm ?? "?"} · shared by {d.shared_by} passage{d.shared_by === 1 ? "" : "s"} · text hash{" "}
                <span className="font-mono">{d.text_hash?.slice(0, 10)}</span>
              </p>
              <p className="mt-0.5 break-all font-mono text-[11px] text-zinc-500">[{d.preview.join(", ")}{(d.dim ?? 0) > d.preview.length ? ", ..." : ""}]</p>
            </>
          ) : (
            <p className="text-amber-800">No vector stored for this text yet: it is found by keyword search only.</p>
          )}
        </div>
      ) : null}
    </div>
  );
}

/** The meaning search of the question: the embedding call and which passages came nearest. */
export function VectorSection({ trace }: { trace: QuestionTrace }) {
  const v = trace.vector;
  if (!v) return <NotRecorded />;
  if (!v.enabled) return <p className="text-xs text-zinc-600">Meaning search was switched off: this question was answered by keyword search alone.</p>;
  const byRow = new Map((trace.passages ?? []).filter((p) => p.row_id).map((p) => [p.row_id as string, p]));
  const kept = (v.nearest ?? []).filter((n) => byRow.has(n.row_id)).length;
  return (
    <div className="space-y-2 text-xs text-zinc-700">
      <div className="flex flex-wrap gap-1.5">
        <Tag>{v.model ?? "model unknown"}</Tag>
        {v.dim ? <Tag>{v.dim} numbers</Tag> : null}
        {v.provider ? <Tag tone={v.provider === "vertex" ? "amber" : "zinc"}>{v.provider === "vertex" ? "served by Vertex AI" : "served by AI Studio"}</Tag> : null}
        {v.error ? <Tag tone="rose">failed: keyword search only</Tag> : <Tag tone="emerald">{v.hits ?? 0} nearest texts</Tag>}
        {!v.error ? <Tag>{kept} reached the writer</Tag> : null}
      </div>
      {v.query ? <p className="text-zinc-500">Embedded: {v.query}</p> : null}
      {v.error ? <p className="text-rose-700">{v.error}</p> : null}
      {v.nearest?.length ? (
        <table className="w-full text-left">
          <thead className="text-zinc-500">
            <tr>
              <th className="py-1 pr-2 font-medium">Rank</th>
              <th className="pr-2 font-medium">Similarity</th>
              <th className="pr-2 font-medium">Passage</th>
              <th className="font-medium">Writer saw it</th>
            </tr>
          </thead>
          <tbody>
            {v.nearest.map((n) => {
              const p = byRow.get(n.row_id);
              return (
                <tr key={n.row_id} className="border-t border-zinc-100 align-top">
                  <td className="py-1 pr-2">{n.rank + 1}</td>
                  <td className="pr-2">{n.sim}</td>
                  <td className="pr-2">{p ? `${p.file} p.${p.page ?? "?"} ${p.section_path || p.where}` : <span className="font-mono text-zinc-400">{n.row_id.slice(0, 8)}</span>}</td>
                  <td>{p ? <span className="text-emerald-700">yes</span> : <span className="text-zinc-400">no (dropped or ranked out)</span>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : null}
    </div>
  );
}

const LAYER_NAMES: Record<string, string> = { rule: "Regulations", order: "Commission orders", contract: "Contracts" };

/** The last part of a file name, without the folder-style prefixes the sources carry. */
function shortFile(name: string): string {
  return (name || "").split("/").pop()!.split("_").pop() || name;
}

type RuleState = "matched" | "flag" | "none" | "unknown";

interface RuleLine {
  name: string;
  what: string;
  state: RuleState;
  detail?: React.ReactNode;
  /** The passages behind this rule, each openable whole. */
  passages?: TracePassage[];
  /** Other things behind this rule (facts, notes, figures), one line each. */
  items?: string[];
}

const SHAPE_WORDS: Record<string, string> = {
  short: "Short: the figure or the direct answer first, with little else.",
  classic: "Classic: the full seven-part explanation.",
  structured: "Structured: a table or list laid out by the question.",
  auto: "Auto: the shape follows the question.",
};

const STATE_LABEL: Record<RuleState, { text: string; tone: Tone }> = {
  matched: { text: "Matched", tone: "emerald" },
  flag: { text: "Look at this", tone: "amber" },
  none: { text: "Did not apply", tone: "zinc" },
  unknown: { text: "Not recorded", tone: "zinc" },
};

/** Every rule the code applies to a question, in plain words, with whether it matched this one. Built only from what the trace recorded. */
function ruleLines(trace: QuestionTrace): RuleLine[] {
  const passages = trace.passages ?? [];
  const dropped = trace.dropped;
  const count = (k: keyof NonNullable<QuestionTrace["dropped"]>) => dropped?.[k]?.length ?? 0;
  const searched = !trace.answered_from;
  const lines: RuleLine[] = [];

  lines.push({
    name: "Say how clear the question is",
    what: "Looks for a contract, state or party, a year, and a thing to do (find, calculate, list). A question with none of them is called abstract and the search is broad.",
    state: trace.label ? (trace.label.quality === "well-formed" ? "matched" : "flag") : "unknown",
    detail: trace.label ? `${trace.label.quality}: ${trace.label.reason}` : undefined,
  });
  lines.push({
    name: "Work out what it is about",
    what: "Reads the contract, state, financial year and subject out of the words, so only those documents are searched.",
    state: trace.scope ? ((trace.scope.contracts?.length || trace.scope.states?.length || trace.scope.periods?.length) ? "matched" : "none") : "unknown",
    detail: trace.scope
      ? [
          trace.scope.contracts?.length ? `contracts ${trace.scope.contracts.join(", ")}` : "",
          trace.scope.states?.length ? `states ${trace.scope.states.join(", ")}` : "",
          trace.scope.periods?.length ? `periods ${trace.scope.periods.join(", ")}` : "",
        ].filter(Boolean).join(" · ") || "nothing named, so every document is in scope"
      : undefined,
  });
  lines.push({
    name: "Take the contract from the question before",
    what: "A follow-up such as \"when was it filed?\" takes the matter of the earlier question in the chat.",
    state: trace.scope ? (trace.scope.borrowed_from_earlier ? "matched" : "none") : "unknown",
    detail: trace.scope?.borrowed_from_earlier ?? undefined,
  });
  lines.push({
    name: "Ask the user to be clearer",
    what: "When the question is too open to answer safely, HP Genie asks back instead of guessing.",
    state: trace.scope ? (trace.scope.clarify?.asked ? "flag" : "none") : "unknown",
    detail: trace.scope?.clarify?.asked ? trace.scope.clarify.reason : undefined,
  });
  lines.push({
    name: "Use a saved answer",
    what: "A question on a saved card, note or the disputes sheet is answered from it, with no search and no model.",
    state: trace.answered_from ? "matched" : "none",
    detail: trace.answered_from,
  });
  lines.push({
    name: "Serve from the answer cache",
    what: "The same question in the same chat is answered again from the database while nothing it rests on has changed.",
    state: trace.cache ? (trace.cache.status === "hit" ? "matched" : "none") : trace.answer_cache === "hit" ? "matched" : "unknown",
    detail: trace.cache ? `cache ${trace.cache.status}${trace.cache.logic_version ? ` · logic ${trace.cache.logic_version}` : ""}` : undefined,
  });
  lines.push({
    name: "Search the documents by words",
    what: "Looks for the question's words and the figures that must appear in a passage.",
    state: searched ? (trace.tokens ? "matched" : "unknown") : "none",
    detail: trace.tokens ? `words: ${trace.tokens.words.join(", ") || "none"}${trace.tokens.exact.length ? ` · must appear: ${trace.tokens.exact.join(", ")}` : ""}` : undefined,
  });
  lines.push({
    name: "Search by meaning (vector)",
    what: "Finds passages that say the same thing in other words, and adds them to the word search.",
    state: trace.vector ? (trace.vector.enabled ? ((trace.vector.hits ?? 0) > 0 ? "matched" : trace.vector.error ? "flag" : "none") : "none") : "unknown",
    detail: trace.vector ? (trace.vector.enabled ? (trace.vector.error ? `failed: ${trace.vector.error}` : `${trace.vector.hits ?? 0} nearest passages`) : "switched off") : undefined,
  });
  lines.push({
    name: "Follow references to other paragraphs",
    what: "A passage that says \"as per clause 5\" brings clause 5 in.",
    state: searched ? ((trace.references?.length ?? 0) > 0 ? "matched" : "none") : "none",
    detail: trace.references?.length ? `${trace.references.length} paragraphs followed` : undefined,
  });
  lines.push({
    name: "Read the whole section for a concept",
    what: "A question about a defined idea (for example heat rate) takes the section that defines it.",
    state: searched ? ((trace.concept_sections ?? 0) > 0 ? "matched" : "none") : "none",
    detail: trace.concept_sections ? `${trace.concept_sections} sections` : undefined,
  });
  lines.push({
    name: "Keep pinned passages",
    what: "Passages an admin pinned to a topic always reach the writer.",
    state: searched ? (passages.some((p) => p.pinned) ? "matched" : "none") : "none",
    detail: passages.some((p) => p.pinned) ? `${passages.filter((p) => p.pinned).length} pinned` : undefined,
  });
  lines.push({
    name: "Mark a party's argument",
    what: "A passage where a petitioner or respondent argues is labelled so it is not read as the commission's ruling.",
    state: searched ? (passages.some((p) => p.party) ? "matched" : "none") : "none",
    detail: passages.some((p) => p.party) ? `${passages.filter((p) => p.party).length} passages` : undefined,
  });
  const dropRule = (name: string, what: string, key: keyof NonNullable<QuestionTrace["dropped"]>): RuleLine => ({
    name, what, state: dropped ? (count(key) > 0 ? "matched" : "none") : searched ? "unknown" : "none", detail: count(key) ? `${count(key)} passages dropped` : undefined,
  });
  lines.push(dropRule("Drop boilerplate", "Cover pages, indexes and notices that say nothing about the question are left out.", "boilerplate"));
  lines.push(dropRule("Drop what a later rule replaced", "An older regulation clause that a newer one replaced is left out.", "superseded"));
  lines.push(dropRule("Drop tender documents", "Bid papers are not read for an answer about the signed contract.", "tender"));
  lines.push(dropRule("Keep to the contract asked about", "Passages from another contract than the one named are left out.", "contract_filter"));
  lines.push(dropRule("Cap what the writer reads", "Only the best-ranked passages go to the writer; the rest are cut.", "cap"));
  lines.push({
    name: "Cover every contract or year asked about",
    what: "When a question names several contracts, states or years, each one needs its own passages.",
    state: trace.coverage?.length ? (trace.coverage.some((r) => r.missing) ? "flag" : "matched") : "none",
    detail: trace.coverage?.some((r) => r.missing) ? `nothing found for ${trace.coverage.filter((r) => r.missing).map((r) => r.subject).join(", ")}` : undefined,
  });
  lines.push({
    name: "Offer facts read from the documents",
    what: "Facts the system checked at ingestion (a rate, a date, a limit) are handed to the writer with their source.",
    state: trace.facts ? ((trace.facts.offered?.length ?? 0) + (trace.facts.registry?.length ?? 0) > 0 ? "matched" : "none") : "unknown",
    detail: trace.facts ? `${trace.facts.offered?.length ?? 0} facts, ${trace.facts.registry?.length ?? 0} agreement or sheet rows, ${trace.facts.used_ids?.length ?? 0} used in the answer` : undefined,
  });
  lines.push({
    name: "Calculate with a formula",
    what: "A sum such as a rebate or a surcharge is worked out by code from the figures in the contract, not by the model.",
    state: trace.facts ? ((trace.facts.calculators?.length ?? 0) > 0 ? "matched" : "none") : "unknown",
    detail: trace.facts?.calculators?.length ? trace.facts.calculators.map((c) => `${c.name} = ${JSON.stringify(c.result ?? null)}`).join(" · ") : undefined,
  });
  lines.push({
    name: "Shape the answer",
    what: "Chooses how long and how structured the answer is.",
    state: trace.writer?.shape ? "matched" : searched ? "unknown" : "none",
    detail: trace.writer?.shape,
  });
  const guard = trace.citations?.guard_notes ?? [];
  lines.push({
    name: "Check each figure against its source",
    what: "A figure in the answer must appear in the passage cited for it; if not, the answer says the source could not be matched.",
    state: trace.citations ? (guard.length ? "flag" : "matched") : "unknown",
    detail: guard.length ? guard.map((g) => `${g.claim} (${g.reason})`).join(" · ") : undefined,
  });
  const strayed = trace.citations?.not_in_passages ?? [];
  lines.push({
    name: "Cite only what the writer saw",
    what: "A citation to a passage the writer was never given is removed or flagged.",
    state: trace.citations ? (strayed.length ? "flag" : "matched") : "unknown",
    detail: strayed.length ? `cited but not seen: ${strayed.join(", ")}` : undefined,
  });
  const sectionRows = passages.filter((p) => p.why === "concept");
  const nearRows = new Set((trace.vector?.nearest ?? []).map((n) => n.row_id));
  const lead = trace.facts?.used_ids ?? [];
  const extras: Record<string, Partial<RuleLine>> = {
    "Say how clear the question is": { items: trace.label ? [`Classifier reason: ${trace.label.reason}`, `Names a contract, state or party: ${trace.label.entity ? "yes" : "no"}`, `Names a year: ${trace.label.period ? "yes" : "no"}`, `Asks for something to be done: ${trace.label.task ? "yes" : "no"}`] : [] },
    "Work out what it is about": { items: trace.scope ? [`Contracts: ${trace.scope.contracts?.join(", ") || "none"}`, `States: ${trace.scope.states?.join(", ") || "none"}`, `Years: ${trace.scope.periods?.join(", ") || "none"}`, `Subjects: ${trace.scope.subjects?.join(", ") || "none"}`, `As of: ${trace.scope.as_of ?? "not set"}`] : [] },
    "Take the contract from the question before": { items: trace.scope?.borrowed_from_earlier ? [trace.scope.borrowed_from_earlier] : [] },
    "Ask the user to be clearer": { items: trace.scope?.clarify?.asked ? [trace.scope.clarify.reason] : [] },
    "Search the documents by words": { passages: trace.first_search ?? [], items: trace.tokens ? [`Words: ${trace.tokens.words.join(", ") || "none"}`, `Must appear: ${trace.tokens.exact.join(", ") || "none"}`] : [] },
    "Search by meaning (vector)": { passages: passages.filter((p) => p.row_id && nearRows.has(p.row_id)), items: trace.vector?.enabled ? [`Model ${trace.vector.model ?? "unknown"}${trace.vector.provider ? `, served by ${trace.vector.provider}` : ""}`, ...(trace.vector.query ? [`Embedded: ${trace.vector.query}`] : [])] : [] },
    "Follow references to other paragraphs": { passages: trace.references ?? [] },
    "Read the whole section for a concept": { passages: sectionRows.length ? sectionRows : passages.filter((p) => p.kind === "section") },
    "Keep pinned passages": { passages: passages.filter((p) => p.pinned) },
    "Mark a party's argument": { passages: passages.filter((p) => p.party) },
    "Drop boilerplate": { passages: dropped?.boilerplate ?? [] },
    "Drop what a later rule replaced": { passages: dropped?.superseded ?? [] },
    "Drop tender documents": { passages: dropped?.tender ?? [] },
    "Keep to the contract asked about": { passages: dropped?.contract_filter ?? [] },
    "Cap what the writer reads": { passages: dropped?.cap ?? [] },
    "Cover every contract or year asked about": { items: (trace.coverage ?? []).map((r) => `${r.subject} (${r.kind}): ${r.passages} passages${r.missing ? ", NOTHING FOUND" : ""}${r.files?.length ? ` · ${r.files.join(", ")}` : ""}`) },
    "Offer facts read from the documents": {
      items: [
        ...(trace.facts?.offered ?? []).map((f) => `${f.subject ?? f.fact_type ?? "fact"}: ${f.value ?? "?"}${f.status ? ` (${f.status})` : ""}${f.file ? ` · ${shortFile(f.file)}` : ""}${f.page ? ` p.${f.page}` : ""}${lead.includes(f.id) ? " · used in the answer" : ""}`),
        ...(trace.facts?.registry ?? []).map((r) => `${r.kind}: ${r.detail}`),
      ],
    },
    "Calculate with a formula": { items: (trace.facts?.calculators ?? []).map((c) => `${c.name}: ${JSON.stringify(c.inputs ?? null)} gives ${JSON.stringify(c.result ?? null)}`) },
    "Shape the answer": { items: trace.writer?.shape ? [SHAPE_WORDS[trace.writer.shape] ?? trace.writer.shape] : [] },
    "Check each figure against its source": { items: guard.map((g) => `${g.claim} (${g.reason})`) },
    "Cite only what the writer saw": { items: strayed },
  };
  return lines.map((l) => ({ ...l, ...(extras[l.name] ?? {}) }));
}

/** The rules and labels that shaped this answer, in plain words: how the question was read, where the passages came from, and each rule with whether it matched. */
export function RulesAndLabelling({ trace }: { trace: QuestionTrace }) {
  const { label } = trace;
  const passages = trace.passages ?? [];
  const lines = ruleLines(trace);
  const [openRules, setOpenRules] = React.useState<Set<string>>(new Set());
  const [openPassage, setOpenPassage] = React.useState<string | null>(null);
  const toggleRule = (name: string) => setOpenRules((prev) => { const next = new Set(prev); if (next.has(name)) next.delete(name); else next.add(name); return next; });
  const matched = lines.filter((l) => l.state === "matched" || l.state === "flag");
  const byLayer = passages.reduce<Record<string, TracePassage[]>>((m, p) => ({ ...m, [p.layer || "other"]: [...(m[p.layer || "other"] ?? []), p] }), {});
  const yes = (b?: boolean) => (b === undefined ? <Tag>not recorded</Tag> : b ? <Tag tone="emerald">yes</Tag> : <Tag tone="rose">no</Tag>);
  return (
    <div className="space-y-4 text-xs text-zinc-700">
      {trace.answered_from ? (
        <p className="rounded-md bg-sky-50 px-3 py-2 text-sky-900">
          Answered from {trace.answered_from}. No search and no model call were made, so there are no passages; below is how the question was read and which saved facts were used.
        </p>
      ) : null}

      <section className="space-y-1">
        <h4 className="font-semibold text-zinc-900">1. How the question was labelled</h4>
        {label ? (
          <>
            {label.derived ? <p className="rounded bg-zinc-50 px-2 py-1 text-zinc-500">Not saved when the question was asked: worked out now from the question's words.</p> : null}
            <p>
              <Tag tone={label.quality === "well-formed" ? "emerald" : label.quality === "vague" ? "rose" : "amber"}>{label.quality}</Tag>{" "}
              {label.quality === "well-formed"
                ? "The question names what it is about and what to do, so the search can be narrow."
                : label.quality === "abstract"
                  ? "The question has no contract, state or year, so the search is broad and may pull in the wrong documents."
                  : "The question is too short or unclear to search well."}
            </p>
            <p className="text-zinc-500">The classifier said: {label.reason}</p>
            <ul className="space-y-0.5">
              <li>Names a contract, state or party: {yes(label.entity)}</li>
              <li>Names a year or period: {yes(label.period)}</li>
              <li>Asks for something to be done (find, calculate, list): {yes(label.task)}</li>
            </ul>
          </>
        ) : (
          <p className="text-zinc-500">No label was recorded for this question.</p>
        )}
      </section>

      <section className="space-y-1">
        <h4 className="font-semibold text-zinc-900">2. What it was taken to be about</h4>
        {trace.scope ? (
          <ul className="list-disc pl-5">
            <li>Contracts: {trace.scope.contracts?.length ? trace.scope.contracts.join(", ") : "none named"}</li>
            <li>States: {trace.scope.states?.length ? trace.scope.states.join(", ") : "none"}</li>
            <li>Years: {trace.scope.periods?.length ? trace.scope.periods.join(", ") : "none"}</li>
            <li>Subjects: {trace.scope.subjects?.length ? trace.scope.subjects.join(", ") : "none"}</li>
            <li>Rules in force on: {trace.scope.as_of ?? "no date set (the latest version is used)"}</li>
          </ul>
        ) : (
          <p className="text-zinc-500">No scope was recorded for this question.</p>
        )}
      </section>

      <section className="space-y-1">
        <h4 className="font-semibold text-zinc-900">3. Where the passages came from</h4>
        {passages.length ? (
          <div className="space-y-2">
            {Object.entries(byLayer).map(([layer, items]) => (
              <div key={layer}>
                <p className="font-medium text-zinc-800">
                  {LAYER_NAMES[layer] ?? layer} <span className="font-normal text-zinc-500">({items.length})</span>
                </p>
                <ul className="list-disc pl-5">
                  {items.map((p, i) => (
                    <li key={`${p.key}-${i}`} title={p.file}>
                      {shortFile(p.file)} · page {p.page ?? "?"} · {p.section_path || p.where || "no clause"}
                      {p.party ? " · a party's argument" : ""}
                      {p.pinned ? " · pinned" : ""}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-zinc-500">{trace.answered_from ? "None: this was not searched." : "No passage reached the writer."}</p>
        )}
      </section>

      <section className="space-y-1">
        <h4 className="font-semibold text-zinc-900">
          4. Every rule, and whether it matched <span className="font-normal text-zinc-500">({matched.length} of {lines.length} matched)</span>
        </h4>
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="text-zinc-500">
              <tr>
                <th className="py-1 pr-3 font-medium">Rule</th>
                <th className="pr-3 font-medium">In plain words</th>
                <th className="pr-3 font-medium">This question</th>
              </tr>
            </thead>
            <tbody>
              {[...lines].sort((a, b) => Number(b.state === "flag") - Number(a.state === "flag") || Number(b.state === "matched") - Number(a.state === "matched")).map((l) => {
                const more = (l.passages?.length ?? 0) + (l.items?.length ?? 0);
                const open = openRules.has(l.name);
                const text = l.detail ?? (more ? "Show what is behind this" : null);
                return (
                  <React.Fragment key={l.name}>
                    <tr className="border-t border-zinc-100 align-top">
                      <td className="py-1.5 pr-3 font-medium text-zinc-800">{l.name}</td>
                      <td className="pr-3 text-zinc-600">{l.what}</td>
                      <td className="pr-3">
                        <Tag tone={STATE_LABEL[l.state].tone}>{STATE_LABEL[l.state].text}</Tag>
                        {text ? (
                          more ? (
                            <button
                              type="button"
                              onClick={() => toggleRule(l.name)}
                              aria-expanded={open}
                              className={["mt-0.5 block break-words text-left underline decoration-dotted hover:text-zinc-900", l.state === "flag" ? "text-amber-800" : "text-zinc-500"].join(" ")}
                            >
                              {text} {open ? "▲" : "▼"}
                            </button>
                          ) : (
                            <div className={["mt-0.5 break-words", l.state === "flag" ? "text-amber-800" : "text-zinc-500"].join(" ")}>{text}</div>
                          )
                        ) : null}
                      </td>
                    </tr>
                    {open && more ? (
                      <tr>
                        <td colSpan={3} className="bg-zinc-50 px-3 py-2">
                          {l.items?.length ? (
                            <ul className="list-disc space-y-0.5 pl-4 text-zinc-700">
                              {l.items.map((it, i) => (
                                <li key={i} className="break-words">{it}</li>
                              ))}
                            </ul>
                          ) : null}
                          {l.passages?.length ? (
                            <ul className="mt-1 space-y-1">
                              {l.passages.map((p, i) => {
                                const id = `${l.name}-${p.key}-${i}`;
                                return (
                                  <li key={id}>
                                    <button type="button" onClick={() => setOpenPassage((v) => (v === id ? null : id))} className="text-left text-zinc-800 hover:underline" title={p.file}>
                                      <span className="font-mono text-zinc-400">{p.key}</span> {shortFile(p.file)} · page {p.page ?? "?"} · {p.section_path || p.where || "no clause"}
                                    </button>
                                    {openPassage === id ? (
                                      <div className="mt-1 rounded border border-zinc-200 bg-white px-2 py-1.5">
                                        <ChunkDetailBox passage={p} />
                                      </div>
                                    ) : null}
                                  </li>
                                );
                              })}
                            </ul>
                          ) : null}
                        </td>
                      </tr>
                    ) : null}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

export function CoverageMatrix({ rows }: { rows: TraceCoverageRow[] | undefined }) {
  if (!rows?.length) return <p className="text-xs text-zinc-500">Coverage not recorded for this question.</p>;
  return (
    <table className="w-full text-left text-xs text-zinc-700">
      <thead className="text-zinc-500">
        <tr>
          <th className="py-1 pr-2 font-medium">Subject</th>
          <th className="pr-2 font-medium">Kind</th>
          <th className="pr-2 font-medium">Asked</th>
          <th className="pr-2 font-medium">Passages</th>
          <th className="font-medium">Files</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} className={["border-t border-zinc-100 align-top", r.missing ? "bg-rose-50 text-rose-800" : ""].join(" ")}>
            <td className="py-1 pr-2 font-medium">{r.subject}</td>
            <td className="pr-2">{r.kind}</td>
            <td className="pr-2">{r.asked ?? ""}</td>
            <td className="pr-2">{r.passages}{r.missing ? " (missing)" : ""}</td>
            <td>{(r.files ?? []).join(", ")}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function FactsSection({ trace }: { trace: QuestionTrace }) {
  const f = trace.facts;
  if (!f) return <NotRecorded />;
  const used = new Set(f.used_ids ?? []);
  const statusTone = (s?: string): Tone => (!s ? "zinc" : /verif|confirm|ok|approved/i.test(s) ? "emerald" : /conflict|reject|wrong|stale/i.test(s) ? "rose" : "amber");
  return (
    <div className="space-y-2 text-xs text-zinc-700">
      {f.offered?.length ? (
        <table className="w-full text-left">
          <thead className="text-zinc-500">
            <tr>
              <th className="py-1 pr-2 font-medium">Used</th>
              <th className="pr-2 font-medium">Contract</th>
              <th className="pr-2 font-medium">Fact</th>
              <th className="pr-2 font-medium">Value</th>
              <th className="pr-2 font-medium">Status</th>
              <th className="font-medium">Source</th>
            </tr>
          </thead>
          <tbody>
            {f.offered.map((o, i) => (
              <tr key={`${o.id}-${i}`} className="border-t border-zinc-100 align-top">
                <td className="py-1 pr-2">{used.has(o.id) ? <span className="text-emerald-700" aria-label="used in the answer">✓</span> : <span className="text-zinc-300">-</span>}</td>
                <td className="pr-2">{o.contract_id ?? ""}</td>
                <td className="pr-2">{[o.fact_type, o.subject].filter(Boolean).join(": ")}</td>
                <td className="pr-2">{o.value == null ? "" : String(o.value)}</td>
                <td className="pr-2">{o.status ? <Tag tone={statusTone(o.status)}>{o.status}</Tag> : null}</td>
                <td>
                  {o.file ?? ""}
                  {o.page != null ? ` p.${o.page}` : ""}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="text-zinc-500">No document facts were offered to the writer.</p>
      )}
      {f.registry?.length ? (
        <div>
          <p className="font-semibold">Registry rows</p>
          <ul className="list-disc pl-5 text-zinc-600">
            {f.registry.map((r, i) => (
              <li key={i}>
                <span className="font-medium">{r.kind}</span>: {r.detail}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {f.calculators?.length ? (
        <div>
          <p className="font-semibold">Calculators</p>
          <ul className="list-disc pl-5 text-zinc-600">
            {f.calculators.map((c, i) => (
              <li key={i}>
                <span className="font-medium">{c.name}</span>: {JSON.stringify(c.inputs ?? null)} gives {JSON.stringify(c.result ?? null)}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

export function WriterSection({ trace }: { trace: QuestionTrace }) {
  const w = trace.writer;
  if (!w) return <NotRecorded />;
  const cells: [string, React.ReactNode][] = [
    ["Provider", w.provider],
    ["Model", w.model],
    ["Prompt size", w.prompt_chars != null ? `${w.prompt_chars.toLocaleString()} chars` : undefined],
    ["Passages in prompt", w.passages_in_prompt],
    ["Attempts", w.attempts],
    ["Fallback", w.fallback ? <Tag tone="amber">{w.fallback}</Tag> : w.fallback === null ? "none" : undefined],
    ["Shape", w.shape],
    ["Temperature", w.temperature],
    ["Seed", w.seed],
    ["HTTP statuses", w.statuses?.length ? w.statuses.join(", ") : undefined],
    ["Model used", w.used_model ? "yes" : `no${w.notice ? `: ${w.notice}` : ""}`],
  ];
  return (
    <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs sm:grid-cols-3">
      {cells.map(([k, v]) => (
        <div key={k}>
          <dt className="text-zinc-500">{k}</dt>
          <dd className="text-zinc-900">{v === undefined || v === null || v === "" ? <span className="text-zinc-400">not recorded</span> : v}</dd>
        </div>
      ))}
    </dl>
  );
}

export function CitationsSection({ trace }: { trace: QuestionTrace }) {
  const c = trace.citations;
  if (!c) return <NotRecorded />;
  const list = (title: string, items: React.ReactNode[], tone = "text-zinc-600") =>
    items.length ? (
      <div>
        <p className="font-semibold text-zinc-800">{title} ({items.length})</p>
        <ul className={["list-disc pl-5", tone].join(" ")}>
          {items.map((n, i) => (
            <li key={i}>{n}</li>
          ))}
        </ul>
      </div>
    ) : null;
  return (
    <div className="space-y-2 text-xs">
      {list("Cited", (c.cited ?? []).map((x) => `${x.key} ${x.file ?? ""}${x.page != null ? ` p.${x.page}` : ""}${x.where ? ` · ${x.where}` : ""}`))}
      {list("Dropped", (c.dropped ?? []).map((x) => `${x.key}: ${x.reason}`))}
      {list("Backfilled", (c.backfilled ?? []).map((x) => `${x.key} ${x.file ?? ""}${x.page != null ? ` p.${x.page}` : ""}`))}
      {list("Guard notes", (c.guard_notes ?? []).map((x) => `${x.claim}: ${x.reason}`), "text-amber-800")}
      {list("Inline keys stripped", (c.inline_keys_stripped ?? []).map(String))}
      {list("Cited keys not in the passages the writer saw", (c.not_in_passages ?? []).map(String), "text-rose-800")}
      {!(c.cited?.length || c.dropped?.length || c.backfilled?.length || c.guard_notes?.length || c.not_in_passages?.length) ? <p className="text-zinc-500">No citations recorded.</p> : null}
    </div>
  );
}

export function DroppedSection({ trace }: { trace: QuestionTrace }) {
  const d = trace.dropped;
  if (!d) return <NotRecorded />;
  const entries = Object.entries(d).filter(([, v]) => Array.isArray(v) && v.length) as [string, TracePassage[]][];
  if (!entries.length) return <p className="text-xs text-zinc-500">Nothing was dropped.</p>;
  return (
    <div className="space-y-2">
      {entries.map(([reason, items]) => (
        <div key={reason}>
          <p className="text-xs font-semibold text-zinc-800">
            {reason.replace("_", " ")} ({items.length})
          </p>
          <ChunksTable items={items} />
        </div>
      ))}
    </div>
  );
}

export function CacheSection({ trace }: { trace: QuestionTrace }) {
  const c = trace.cache;
  if (!c) return <NotRecorded />;
  return (
    <p className="text-xs text-zinc-700">
      <Tag tone={c.status === "hit" ? "sky" : "zinc"}>{c.status}</Tag>
      {c.key_prefix ? <span className="ml-2 font-mono text-zinc-500">key {c.key_prefix}</span> : null}
      {c.logic_version ? <span className="ml-2 text-zinc-500">logic {c.logic_version}</span> : null}
    </p>
  );
}

/** The observability sections that have something to show: one with no data (not recorded, zero, empty) is left out. */
export function ObservabilitySections({ trace }: { trace: QuestionTrace }) {
  const chunks = trace.passages ?? [];
  const droppedCount = Object.values(trace.dropped ?? {}).reduce((s, v) => s + (Array.isArray(v) ? v.length : 0), 0);
  const c = trace.citations;
  const hasCitations = !!c && [c.cited, c.dropped, c.backfilled, c.guard_notes, c.inline_keys_stripped, c.not_in_passages].some((v) => (v?.length ?? 0) > 0);
  const f = trace.facts;
  const factCount = (f?.offered?.length ?? 0) + (f?.registry?.length ?? 0) + (f?.calculators?.length ?? 0);
  return (
    <div className="space-y-2">
      <Fold title="Rules and labelling" hint="How the question was read (label and scope), where each passage came from, and every rule the code applies with whether it matched this question." open>
        <RulesAndLabelling trace={trace} />
      </Fold>
      {trace.vector ? (
        <Fold title="Meaning search (vector embedding)" hint="A second search that finds passages meaning the same thing in different words. Each passage is stored as a list of numbers (a vector) and the closest ones to the question are added." count={trace.vector.enabled ? `${trace.vector.hits ?? 0} nearest` : undefined} open>
          <VectorSection trace={trace} />
        </Fold>
      ) : null}
      {chunks.length ? (
        <Fold title="Chunks" hint="The passages (chunks of the documents) that were handed to the answer writer. Click a row to read it whole." count={trace.passages_count ?? chunks.length} open>
          <ChunksTable items={chunks} vector={trace.vector} />
        </Fold>
      ) : null}
      {trace.coverage?.length ? (
        <Fold title="Fan-out: coverage" hint="When a question names several contracts, states or years, this shows whether each one found any passages. Red means nothing was found for it." count={`${trace.coverage.filter((r) => r.missing).length} missing of ${trace.coverage.length}`}>
          <CoverageMatrix rows={trace.coverage} />
        </Fold>
      ) : null}
      {factCount ? (
        <Fold title="Facts" hint="Facts read from the documents when they were loaded (a rate, a date, a limit) and given to the writer with their source. Used means the figure appears in the answer." count={factCount}>
          <FactsSection trace={trace} />
        </Fold>
      ) : null}
      {trace.writer ? (
        <Fold title="Writer" hint="The AI model that wrote the answer from the passages: which route and model, how big the prompt was, and whether it had to retry or switch route.">
          <WriterSection trace={trace} />
        </Fold>
      ) : null}
      {hasCitations ? (
        <Fold title="Citations" hint="The sources shown under the answer: which passages were cited, which were dropped, and any figure that could not be matched to its source." count={c?.cited?.length}>
          <CitationsSection trace={trace} />
        </Fold>
      ) : null}
      {droppedCount ? (
        <Fold title="Dropped passages by reason" hint="Passages that were found but not given to the writer, and why (boilerplate, replaced by a newer rule, wrong contract, or over the cap)." count={droppedCount}>
          <DroppedSection trace={trace} />
        </Fold>
      ) : null}
      {trace.cache ? (
        <Fold title="Cache" hint="Whether this answer was served from stored answers (hit) or written fresh (miss).">
          <CacheSection trace={trace} />
        </Fold>
      ) : null}
    </div>
  );
}
